import moment, { Moment } from 'moment';

moment.locale('fr');

type DoctrineDate = Date | string | Moment;

export const getNowDate = () => moment();

export const getDateKey = (date: DoctrineDate) =>
  moment(date).format('YYYY-MM-DD');

export const getFormattedDate = (date: DoctrineDate) =>
  moment(date).format('LL');

export const getShortFormattedDate = (date: DoctrineDate) =>
  moment(date).format('Do MMMM');

export const getShortFormattedDateWithYear = (date: DoctrineDate) =>
  moment(date).format('Do MMM YYYY');

export const getShortFormattedDateWithSlashes = (date: DoctrineDate) =>
  moment(date).format('DD/MM/YYYY');

export const getDiffFromNowWithType = (date: string, type: 'days' | 'year') =>
  moment().startOf(type).diff(moment(date).startOf(type), type);

export const getDateFromString = (dateString: string) => moment(dateString);

export const parseDateWithKeyFormat = (dateString: string) =>
  moment(dateString, 'YYYY-MM-DD');

export const getTimeInLetters = (time: any) =>
  moment.duration(time, 'seconds').humanize();

export const aggregateDate = (day: number, month: string, year: number) =>
  moment()
    .year(year)
    .month(month) // moment.month should be number|string in flow-typed definition
    .date(day)
    .format('DD/MM/YYYY');

const getYesterdayOrTodayDate = (
  pastDays: number,
): "aujourd'hui" | 'hier' | false => {
  if (pastDays === 0) return "aujourd'hui";
  if (pastDays === 1) return 'hier';
  return false;
};

export const formatDateFromNow = (date: string): string => {
  const pastDays = getDiffFromNowWithType(date, 'days');
  const yesterdayOrToday = getYesterdayOrTodayDate(pastDays);
  return yesterdayOrToday || getFormattedDate(moment(date));
};

export const formatShortDateFromNow = (date: string): string => {
  const pastDays = getDiffFromNowWithType(date, 'days');
  const yesterdayOrToday = getYesterdayOrTodayDate(pastDays);
  return yesterdayOrToday || getShortFormattedDate(moment(date));
};

const getPastDaysFormattedDate = (pastDays: number) => {
  if (pastDays <= 1) {
    return `il y a ${pastDays} jour`;
  }
  return `il y a ${pastDays} jours`;
};

export const formatGenericDate = (date: string): string => {
  const pastDays = getDiffFromNowWithType(date, 'days');
  const yesterdayOrToday = getYesterdayOrTodayDate(pastDays);
  if (yesterdayOrToday) {
    return yesterdayOrToday;
  }
  if (pastDays <= 7) {
    return getPastDaysFormattedDate(pastDays);
  }
  const pastYears = getDiffFromNowWithType(date, 'year');
  if (pastYears === 0) {
    return `le ${getShortFormattedDate(date)}`;
  }

  return `le ${getShortFormattedDateWithYear(date)}`;
};

export const getMonth = (n: number): string => moment.months(n - 1);

export const getHours = (date: string): string =>
  moment(date).format('LT').split(':').join('h');

export const formatShortDateWithHours = (date: string): string => {
  const pastDays = getDiffFromNowWithType(date, 'days');
  const yesterdayOrToday = getYesterdayOrTodayDate(pastDays);
  const dateWithYear = getShortFormattedDateWithYear(date);
  const hours = getHours(date);

  if (yesterdayOrToday) {
    return `${yesterdayOrToday} à ${hours}`;
  }
  return `${dateWithYear} à ${hours}`;
};
